const TARGET_WIDTH = 1020;
const TARGET_HEIGHT = 1000;
const TARGET_RATIO = TARGET_WIDTH / TARGET_HEIGHT;

/**
 * Center-crops and resizes a file to exactly 1020×1000 (51:50) using a canvas.
 * Returns the original file unchanged if canvas API is unavailable.
 */
export const enforceImageRatio = (file: File): Promise<File> =>
  new Promise((resolve) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_HEIGHT;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
        return;
      }

      const srcRatio = img.width / img.height;
      let sx = 0;
      let sy = 0;
      let sw = img.width;
      let sh = img.height;

      if (srcRatio > TARGET_RATIO) {
        // Wider than target — trim the sides
        sw = Math.round(img.height * TARGET_RATIO);
        sx = Math.round((img.width - sw) / 2);
      } else {
        // Taller than target — trim top/bottom
        sh = Math.round(img.width / TARGET_RATIO);
        sy = Math.round((img.height - sh) / 2);
      }

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
      URL.revokeObjectURL(objectUrl);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const name = file.name.replace(/\.[^.]+$/, ".jpg");
            resolve(new File([blob], name, { type: "image/jpeg" }));
          } else {
            resolve(file);
          }
        },
        "image/jpeg",
        0.9,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });

/** Reads a File as a base-64 data-URL for use as an <img src>. */
export const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
